import React, { useState } from 'react';
import {
  PrimaryTableRowText,
  TableColumnTitle,
} from '../../../../shared/globalStyling/styledText';
import { Button, message, Space, Table, Typography, Tooltip, Badge } from 'antd';
import {
  TagOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { formatWholeNumberNaN } from '../../../../../core/utils/campaigns';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import CampaignsDrawer from './CampaignsDrawer';

const { Text } = Typography;

// Modern metric card component
const MetricCard = ({ label, value, date, status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          bgColor: '#f6ffed',
          borderColor: '#b7eb8f',
          textColor: '#52c41a',
          icon: <CheckCircleOutlined style={{ fontSize: 10, marginRight: 4 }} />,
        };
      case 'error':
        return {
          bgColor: '#fff2f0',
          borderColor: '#ffccc7',
          textColor: '#ff4d4f',
          icon: <CloseCircleOutlined style={{ fontSize: 10, marginRight: 4 }} />,
        };
      default:
        return {
          bgColor: '#fafafa',
          borderColor: '#d9d9d9',
          textColor: '#8c8c8c',
          icon: <ClockCircleOutlined style={{ fontSize: 10, marginRight: 4 }} />,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Tooltip title={date ? `Last updated: ${date}` : 'Signal Unknown'}>
      <div
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '8px 12px',
          borderRadius: 8,
          backgroundColor: config.bgColor,
          border: `1px solid ${config.borderColor}`,
          minWidth: 80,
          transition: 'all 0.2s ease',
        }}
      >
        <Text
          style={{
            fontSize: 11,
            color: '#8c8c8c',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: 2,
          }}
        >
          {label}
        </Text>
        <Text
          strong
          style={{
            fontSize: 16,
            color: config.textColor,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {config.icon}
          {formatWholeNumberNaN(value)}
        </Text>
      </div>
    </Tooltip>
  );
};

// Get status based on date and value
const getMetricStatus = (date, value) => {
  if (!date) return 'default';
  if (value < 1) return 'error';
  return 'success';
};

const MainTable = ({ tags = [], history, testTag, isAttentionSignals }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTag, setSelectedTag] = useState({ appId: null, name: '' });

  const handleOpenDrawer = (record) => {
    setSelectedTag({ appId: record.appId, name: record.name });
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
  };

  const columns = [
    {
      title: <TableColumnTitle text={'Tag Name'} />,
      key: 'name',
      width: '18%',
      render: (record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="tag-name-link">
            <PrimaryTableRowText text={record.name} />
          </span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.advertiser}
          </Text>
        </div>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    isAttentionSignals
      ? {
          title: <TableColumnTitle text={'Funnels'} />,
          key: 'funnels',
          width: '8%',
          align: 'center',
          render: (record) => (
            <Badge
              count={record.funnels}
              style={{
                backgroundColor: record.funnels > 0 ? '#1890ff' : '#d9d9d9',
              }}
              showZero
            />
          ),
          sorter: (a, b) => a.measuredEvents.funnels - b.measuredEvents.funnels,
        }
      : {},
    {
      title: <TableColumnTitle text={'Campaigns'} />,
      key: 'campaigns',
      width: '12%',
      align: 'center',
      render: (record) => {
        if (!record.appId) return null;
        return (
          <Button
            type="default"
            icon={<EyeOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              handleOpenDrawer(record);
            }}
            style={{
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            View
          </Button>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Measured Events'} />,
      key: 'measuredEvents',
      render: (record) => {
        const { transactions, pageViews, signups, date, isSignUps, sessions } =
          record.measuredEvents;

        const formattedDate = date ? moment(date).format('YYYY/MM/DD - HH:mm') : null;

        return (
          <Space size={8} wrap>
            {pageViews !== undefined && (
              <MetricCard
                label="Page Views"
                value={pageViews}
                date={formattedDate}
                status={getMetricStatus(date, pageViews)}
              />
            )}
            {transactions !== undefined && (
              <MetricCard
                label="Transactions"
                value={transactions}
                date={formattedDate}
                status={getMetricStatus(date, transactions)}
              />
            )}
            {(isSignUps || signups > 0) && (
              <MetricCard
                label="Sign-Ups"
                value={signups}
                date={formattedDate}
                status={getMetricStatus(date, signups)}
              />
            )}
            {sessions > 0 && (
              <MetricCard
                label="Sessions"
                value={sessions}
                date={formattedDate}
                status={getMetricStatus(date, sessions)}
              />
            )}
          </Space>
        );
      },
      sorter: (a, b) => a.measuredEvents.pageViews - b.measuredEvents.pageViews,
    },
    {
      title: <TableColumnTitle text={'Tag ID'} />,
      key: 'appId',
      width: '22%',
      render: (record) => (
        <Typography.Text
          copyable={{
            text: record.appId,
            tooltips: ['Copy Tag ID', 'Copied!'],
          }}
          style={{
            fontFamily: 'monospace',
            fontSize: 12,
            backgroundColor: '#f5f5f5',
            padding: '4px 8px',
            borderRadius: 4,
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {record.appId}
        </Typography.Text>
      ),
      sorter: (a, b) => a.appId.localeCompare(b.appId),
    },
    {
      title: <TableColumnTitle text={'Action'} />,
      key: 'action',
      width: '12%',
      align: 'center',
      render: (record) => (
        <Button
          type="primary"
          icon={<TagOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            testTag({
              appId: record.appId,
              orgId: record.orgId,
              tagId: record.id,
            });
            message.info(
              `Signal Tag Test for ${record.name} initiated. May take a couple of minutes to complete.`
            );
          }}
          style={{
            borderRadius: 6,
          }}
        >
          Test Signal
        </Button>
      ),
    },
  ];

  return (
    <>
      <Table
        dataSource={tags}
        columns={columns.filter((col) => Object.keys(col).length > 0)}
        rowKey={(record) => record.id}
        onRow={(record) => ({
          onClick: () => {
            history.push(`/tags/details/${record.id}?source=measurement`);
          },
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: '100%' }}
        size="middle"
        pagination={{
          showSizeChanger: tags.length > 10,
        }}
        rowClassName={() => 'tag-table-row'}
        style={{
          borderRadius: 8,
        }}
      />
      <CampaignsDrawer
        visible={drawerVisible}
        onClose={handleCloseDrawer}
        tagId={selectedTag.appId}
        tagName={selectedTag.name}
      />
      <style>{`
        .tag-table-row:hover {
          background-color: #fafafa !important;
        }
        .tag-table-row td {
          transition: background-color 0.2s ease;
        }
        .tag-name-link {
          cursor: pointer;
          transition: color 0.2s ease;
        }
        .tag-table-row:hover .tag-name-link div {
          color: #1890ff !important;
        }
      `}</style>
    </>
  );
};

export default withRouter(MainTable);
